/*
    Merrid Controls. Wszelkie prawa zastrzeżone.
    Plik zawiera kody Źródłowe będące własnością firmy Merrid Controls.
    Nie wolno ich rozpowszechniać w całości lub fragmentach bez wyraźnej zgody właściciela.
*/


import React, {useContext, useEffect, useRef, useState} from "react";

import {useDispatch} from "react-redux";
import {useTypedSelector} from "@Services/Hooks/UseTypedSelector";
import CHANGE_API_REQUEST_STATUS_ACTION from "@Actions/Interface/ChangeApiRequestStatus";

import ApplicationApiContext from "@Contexts/Api";

import APPLICATION_ROUTER_INTERFACE from "@ROUTER/Interfaces/Router";
import ApplicationRouterContext from "@Contexts/Router";

import LOGGED_USER_INTERFACE from "@Store/Interfaces/Authentication/User";

import USER_INTERFACE from "@Services/Application/Interfaces/Users/User";

import {HTTP_ERROR_RESPONSE_INTERFACE} from "@Services/Interfaces/Http/Response/Error";
import {ERROR_SYSTEM_MESSAGE_INTERFACE} from "@Services/Application/Interfaces/_Messages/Errors";


import DEFAULT_REQUEST_HEADERS from "@Services/Interfaces/Http/Request/Headers";


import dateFormat from "dateformat";


import {Redirect} from "react-router-dom";


import {Col, Form, Row} from "react-bootstrap";


import BlockContentHeaderComponent from "@Components/UI/Containers/Block/Header";
import BlockContentContainerComponent from "@Components/UI/Containers/Block";


import ErrorMessageComponent from "@Components/UI/Communicates/Messages/Error";


import SettingsSMSCodeVerificationComponent from "@UIPages/Authenticated/Settings/_Parts/Verification";
import validateUserPassword from "@Utilities/Validators/Password";
import FormFieldsGroupComponent from "@Components/UI/Forms/Group";
import FormFieldsGroupHeaderComponent from "@Components/UI/Forms/Group/Header";
import FormFieldComponent from "@Components/UI/Forms/Field";
import DefaultFormButton from "@Components/UI/Forms/Buttons/Default";
import DefaultMessageComponent from "@Components/UI/Communicates/Messages/Default";
import AUTHORIZATION_TOKEN_INTERFACE from "@Reducers/Api/RequestToken";
import REST_API_REQUEST from "@Services/Utilities/Requests";
import CHANGE_MODAL_MESSAGE_ACTION from "@Actions/Interface/ChangeModalMessage";


type ChangePasswordPageProps = {};


export const SettingsChangePasswordPageComponent: React.FC<ChangePasswordPageProps> = (): JSX.Element | null => {


    /** @const Global State Actions Dispatcher */
    const DISPATCHER = useDispatch();


    /** @const {React.Ref} My Favourite Terminals Page Container DOM Ref */
    const COMPONENT_REF: React.Ref<HTMLDivElement | null> = useRef(null);


    /** @const {UserInterface | null} Application Logged User (from Global Store Object) */
    const LOGGED_USER: LOGGED_USER_INTERFACE = useTypedSelector(
        (state): ReturnType<any> => state?.authentication
    )?.loggedUser || null;


    /** @const {string} Global Rest Api Root URL Address */
    const REST_API_URL = useContext(ApplicationApiContext)?.API_ROOT_URL || null;

    /** @const {object} REST_API_DATA - Application State Store Rest Api Data */
    const REST_API_DATA:ReturnType<any> = useTypedSelector(
        (state) => state.api
    );

    /** @const {} REST_API_REQUEST_TOKEN - Application Rest Api Request Token */
    const REST_API_REQUEST_TOKEN: AUTHORIZATION_TOKEN_INTERFACE = REST_API_DATA?.requestToken || null;


    const REST_API_REQUEST_STATUS= useTypedSelector(
        (state):ReturnType<any> => state.interface
    )?.apiRequestStatus;


    const ROUTER: APPLICATION_ROUTER_INTERFACE = useContext(ApplicationRouterContext);


    const [userVerifiedStatus, setUserVerifiedStatus] = useState<boolean>(false);

    const userVerifiedStatusHandler = (response: any) => {
        const {OperationGUID: operationGuid} = response;
        setOperationGuid(operationGuid);
    };

    const [operationGuid, setOperationGuid] = useState<string>("");

    useEffect(() => {
        setUserVerifiedStatus(operationGuid ? true : false);
    }, [operationGuid]);


    const [userNewPasswordError, setUserNewPasswordError] = useState<any>({});
    const [userNewPasswordSubmitSuccess, setUserNewPasswordSubmitSuccess] = useState<boolean>(false);

    const [userNewPassword, setUserNewPassword] = useState<string>("");
    const [userNewPasswordRepeat, setUserNewPasswordRepeat] = useState<string>("");

    const changeUserNewPassword = (e: React.ChangeEvent<HTMLInputElement>) => {
        const password = e.target.value;
        if (validateUserPassword(password)) setUserNewPasswordError({...userNewPasswordError, newPassword: null});
        setUserNewPassword(password);
    };

    const changeUserNewPasswordRepeat = (e: React.ChangeEvent<HTMLInputElement>) => {
        const password = e.target.value;
        if (userNewPassword === password) setUserNewPasswordError({...userNewPasswordError, newPasswordRepeat: null});
        setUserNewPasswordRepeat(password);
    };

    useEffect(() => {
        return () => {
            resetChangePasswordState();
        };
    }, []);

    const resetChangePasswordState = () => {
        setUserNewPassword("");
        setUserNewPasswordRepeat("");
        setUserNewPasswordSubmitSuccess(false);
        setUserNewPasswordError({});
        setOperationGuid("");
    };


    if (!LOGGED_USER || !LOGGED_USER?.userID) return <Redirect to={"/logowanie"}/>;


    if (!ROUTER) return <Redirect to={"/404"}/>;


    if (!userVerifiedStatus) {
        return (
            <SettingsSMSCodeVerificationComponent
                verificationStatusHandler={userVerifiedStatusHandler}
                requestStep1URL={"/User/ChangePwd/Initialization"}
                requestStep2URL={"/User/ChangePwd/Step1"}
            />
        );
    }


    const NEW_PASSWORD_SUBMIT_HANDLER = async () => {

        const errors: any = {};

        if (!validateUserPassword(userNewPassword)) errors.newPassword = "Podane hasło ma nieprawidłowy format";
        if (userNewPassword !== userNewPasswordRepeat) errors.newPasswordRepeat = "Podane hasła różnią się";

        setUserNewPasswordError(errors);

        if (Object.entries(errors).length) return null;

        await NEW_PASSWORD_REQUEST_HANDLER();

    };


    const NEW_PASSWORD_REQUEST_HANDLER = async () => {

        if (!REST_API_URL || !LOGGED_USER?.userID) return; // No Valid Data - Stop

        const CHANGE_PASSWORD_API_URL: string = REST_API_URL + "/User/ChangePwd/Finalization";

        const CHANGE_PASSWORD_REQUEST_DATA = {
            UserId: LOGGED_USER?.userID, // Logged User ID
            OperationGUID: operationGuid,
            Password: btoa(userNewPassword)
        };

        DISPATCHER(CHANGE_API_REQUEST_STATUS_ACTION(true));

        try {

            const CHANGE_PASSWORD_RESPONSE = await REST_API_REQUEST(
                CHANGE_PASSWORD_API_URL,
                CHANGE_PASSWORD_REQUEST_DATA,
                REST_API_REQUEST_TOKEN
            );

            const CONTENT = await CHANGE_PASSWORD_RESPONSE.text();

            if (CHANGE_PASSWORD_RESPONSE.status === 204) {
                DISPATCHER(CHANGE_MODAL_MESSAGE_ACTION(`Hasło zostało zmienione`, "SUCCESS"));
                setUserNewPasswordSubmitSuccess(true);
            } else {
                setUserNewPasswordError({process: JSON.parse(CONTENT)?.UserMessage})
            }

        } catch (e) {

        }

        DISPATCHER(CHANGE_API_REQUEST_STATUS_ACTION(false));

    };


    if (userNewPasswordSubmitSuccess === true) {
        return <Redirect to={ROUTER.AUTHENTICATED.SETTINGS.MAIN_PAGE.URL} />;
    }

    return (
        <BlockContentContainerComponent forwardRef={COMPONENT_REF} cssClasses={"settings-page-account-component settings-page-view-component"}>

            <BlockContentHeaderComponent headerText={"Zmień hasło"} />

            <Col as={Form} xs={12} className={"user-form-component"}>
                <Row className={"user-form-content"}>

                    <FormFieldsGroupComponent>

                        <FormFieldsGroupHeaderComponent headerText={`Podaj nowe hasło`} />

                        <FormFieldComponent
                            lg={6}
                            fieldValue={userNewPassword}
                            fieldValueHandler={e => changeUserNewPassword(e)}
                            fieldType={"password"} fieldLabel={`Hasło`} fieldControlID={`form-user-password`}
                            fieldDisabledStatus={!!REST_API_REQUEST_STATUS}
                            fieldError={userNewPasswordError?.newPassword}
                            fieldLegend={`Hasło powinno składać się z minimum 8 znaków, oraz zawierać cyfrę, wielką literę i znak specjalny`}
                        />

                        <FormFieldComponent
                            lg={6}
                            fieldValue={userNewPasswordRepeat}
                            fieldValueHandler={e => changeUserNewPasswordRepeat(e)}
                            fieldType={"password"} fieldLabel={`Powtórz hasło`} fieldControlID={`form-user-password-repeat`}
                            fieldDisabledStatus={!!REST_API_REQUEST_STATUS}
                            fieldError={userNewPasswordError?.newPasswordRepeat}
                        />

                    </FormFieldsGroupComponent>

                    <DefaultFormButton
                        buttonText={`Zmień Hasło`}
                        clickHandler={() => NEW_PASSWORD_SUBMIT_HANDLER()}
                        controlId={`change-password-submit`}
                        disabled={!!REST_API_REQUEST_STATUS}
                        cssClasses={`change-password-submit`}
                    />

                </Row>
            </Col>

        </BlockContentContainerComponent>
    );

}


export default SettingsChangePasswordPageComponent;
